import { useState } from "react";

interface Option {
  id: string;
  label: string;
  icon: string;
}

interface DiagnosisStepProps {
  stepNum: number;
  totalSteps: number;
  question: string;
  subText: string;
  options: Option[];
  selected: string | null;
  onSelect: (id: string) => void;
  onNext: () => void;
  visible: boolean;
  isDark?: boolean;
}

function OptionCard({ opt, i, isSelected, isDark, onSelect }: {
  opt: Option;
  i: number;
  isSelected: boolean;
  isDark: boolean;
  onSelect: (id: string) => void;
}) {
  const [pressed, setPressed] = useState(false);

  const handleClick = () => {
    setPressed(true);
    onSelect(opt.id);
    setTimeout(() => setPressed(false), 200);
  };

  return (
    <button
      onClick={handleClick}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setTimeout(() => setPressed(false), 200)}
      className="w-full flex items-center gap-3 md:gap-5 text-left cursor-pointer"
      style={{
        padding: "14px 16px",
        border: `1.5px solid ${isSelected ? "#c00000" : pressed ? "#c00000" : (isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)")}`,
        background: isSelected
          ? (isDark ? "rgba(192,0,0,0.18)" : "rgba(192,0,0,0.06)")
          : pressed
            ? (isDark ? "rgba(192,0,0,0.1)" : "rgba(192,0,0,0.03)")
            : (isDark ? "rgba(255,255,255,0.04)" : "#ffffff"),
        opacity: 0,
        animation: `optIn 0.5s cubic-bezier(0.22,1,0.36,1) ${0.05 + i * 0.06}s both`,
        transform: pressed && !isSelected ? "scale(0.98)" : "scale(1)",
        transition: "border 0.2s ease, background 0.2s ease, transform 0.15s ease",
        position: "relative",
        overflow: "hidden",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* Left accent bar */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "3px",
          background: "#c00000",
          transform: isSelected ? "scaleY(1)" : "scaleY(0)",
          transformOrigin: "top center",
          transition: "transform 0.3s cubic-bezier(0.22,1,0.36,1)",
        }}
      />

      {/* Icon */}
      <div
        className="shrink-0 w-9 h-9 flex items-center justify-center"
        style={{
          color: isSelected ? "#c00000" : (isDark ? "rgba(255,255,255,0.3)" : "#111111"),
          transition: "color 0.2s ease",
        }}
      >
        <i className={`${opt.icon} text-xl`} />
      </div>

      {/* Label */}
      <span
        style={{
          fontFamily: "'Noto Sans JP', sans-serif",
          fontSize: "clamp(14px, 1.1vw, 15px)",
          fontWeight: isSelected ? 700 : 400,
          color: isSelected
            ? (isDark ? "#ffffff" : "#c00000")
            : (isDark ? "rgba(255,255,255,0.85)" : "#111111"),
          lineHeight: 1.6,
          flex: 1,
          transition: "color 0.2s ease",
        }}
      >
        {opt.label}
      </span>

      {/* Check icon when selected */}
      <div
        style={{
          opacity: isSelected ? 1 : 0,
          transform: isSelected ? "scale(1)" : "scale(0.5)",
          transition: "opacity 0.25s ease, transform 0.3s cubic-bezier(0.22,1,0.36,1)",
          color: "#c00000",
          flexShrink: 0,
        }}
      >
        <i className="ri-check-line text-base" />
      </div>
    </button>
  );
}

export default function DiagnosisStep({
  stepNum, totalSteps, question, subText,
  options, selected, onSelect, onNext, visible, isDark = false,
}: DiagnosisStepProps) {
  return (
    <div
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: "opacity 0.5s cubic-bezier(0.22,1,0.36,1), transform 0.5s cubic-bezier(0.22,1,0.36,1)",
      }}
    >
      {/* Step indicator top */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center gap-1.5">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              style={{
                width: i + 1 === stepNum ? "28px" : "8px",
                height: "3px",
                borderRadius: "2px",
                background: i + 1 === stepNum
                  ? "#c00000"
                  : i + 1 < stepNum
                    ? (isDark ? "#ffffff" : "#111111")
                    : (isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)"),
                transition: "all 0.4s ease",
              }}
            />
          ))}
        </div>
        <span
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "11px",
            letterSpacing: "0.2em",
            color: isDark ? "rgba(255,255,255,0.3)" : "#111111",
          }}
        >
          {String(stepNum).padStart(2, "0")} / {String(totalSteps).padStart(2, "0")}
        </span>
      </div>

      {/* Question */}
      <div className="mb-1.5">
        <span
          style={{
            fontFamily: "'Barlow', sans-serif",
            fontSize: "11px",
            letterSpacing: "0.4em",
            color: "#c00000",
          }}
        >
          Q{stepNum}.
        </span>
      </div>
      <h2
        className="mb-2"
        style={{
          fontFamily: "'Noto Sans JP', sans-serif",
          fontSize: "clamp(16px, 4vw, 26px)",
          fontWeight: 700,
          color: isDark ? "#ffffff" : "#111111",
          lineHeight: 1.5,
          letterSpacing: "0.02em",
        }}
      >
        {question}
      </h2>
      <p
        className="mb-7"
        style={{
          fontFamily: "'Noto Sans JP', sans-serif",
          fontSize: "12px",
          color: isDark ? "rgba(255,255,255,0.4)" : "#111111",
          lineHeight: 1.7,
        }}
      >
        {subText}
      </p>

      {/* Options */}
      <div className="flex flex-col gap-2.5 mb-8">
        {options.map((opt, i) => (
          <OptionCard
            key={opt.id}
            opt={opt}
            i={i}
            isSelected={selected === opt.id}
            isDark={isDark}
            onSelect={onSelect}
          />
        ))}
      </div>

      {/* Next button */}
      <button
        onClick={onNext}
        disabled={!selected}
        className="w-full flex items-center justify-center gap-3 py-4 transition-all duration-200 cursor-pointer whitespace-nowrap active:scale-95"
        style={{
          background: selected ? "#c00000" : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
          color: selected ? "#ffffff" : (isDark ? "rgba(255,255,255,0.25)" : "#111111"),
          cursor: selected ? "pointer" : "not-allowed",
          fontFamily: "'Noto Sans JP', sans-serif",
          fontSize: "14px",
          fontWeight: 700,
          letterSpacing: "0.08em",
          border: "none",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {stepNum < totalSteps ? "次の質問へ" : "診断結果を見る"}
        <i className="ri-arrow-right-line text-sm" />
      </button>
    </div>
  );
}
